import { useEffect, useState } from "react";
import { ErrorBoundary } from "react-error-boundary";
import { listDriversForAccount, listRecentDriversForAccount, listCompletedDrivers, fetchDriverMVR } from "../api/driver";
import { Toaster, toast } from 'react-hot-toast';
import { FaTruck, FaCheckCircle, FaClock } from "react-icons/fa";
import { MdPeople } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { RiskBadge } from "../../components/ui";

function ErrorFallback({ error }) {
  return (
    <div className="p-4 bg-red-50 border border-red-400 rounded">
      <h2 className="text-lg font-bold text-red-800">Something went wrong:</h2>
      <pre className="text-red-600">{error.message}</pre>
    </div>
  );
}

const TABS = [
  { id: 'all', label: 'All Drivers', icon: FaTruck, color: 'blue' },
  { id: 'recent', label: 'Recent', icon: FaClock, color: 'amber' },
  { id: 'completed', label: 'Completed', icon: FaCheckCircle, color: 'green' },
];

export default function DriversView({ onViewAnalytics, searchItem }) {
  const [activeTab, setActiveTab] = useState('all');
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [mvrLoading, setMvrLoading] = useState(null);
  const [mvrProgress, setMvrProgress] = useState({});
  const [mvrCompleted, setMvrCompleted] = useState({});
  const [filteredDrivers, setFilteredDrivers] = useState([]);

  const navigate = useNavigate();
  const { token } = useSelector((state) => state.auth);
  const { accountId, entityId } = useSelector((state) => state.account);

  const BUSINESS_BASE_URL = process.env.REACT_APP_BUSINESS_URL || 'https://records.4safedrivers.com';

  // Fetch drivers when tab or accountId changes
  useEffect(() => {
    if (accountId) {
      handleFetchDrivers();
    }
  }, [accountId, activeTab]);

  // Filter drivers based on search
  useEffect(() => {
    const lowerCaseSearchItem = (searchItem || "").toLowerCase();
    if (!lowerCaseSearchItem) {
      setFilteredDrivers(drivers);
      return;
    }
    const newFilteredDrivers = drivers.filter(driver =>
      driver.first_name?.toLowerCase().includes(lowerCaseSearchItem) ||
      driver.last_name?.toLowerCase().includes(lowerCaseSearchItem) ||
      driver.license_state?.toLowerCase().includes(lowerCaseSearchItem) ||
      driver.license_number?.toLowerCase().includes(lowerCaseSearchItem)
    );
    setFilteredDrivers(newFilteredDrivers);
  }, [drivers, searchItem]);

  const handleFetchDrivers = async () => {
    if (!accountId) {
      setError("No account ID available");
      setDrivers([]);
      setFilteredDrivers([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      let data;
      if (activeTab === 'all') {
        data = await listDriversForAccount(accountId);
      } else if (activeTab === 'recent') {
        data = await listRecentDriversForAccount(accountId);
      } else if (activeTab === 'completed') {
        data = await listCompletedDrivers(accountId);
      }

      if (!data) {
        throw new Error("No driver data received");
      }
      setDrivers(data);
    } catch (err) {
      setError(err.message || "Failed to fetch drivers");
      setDrivers([]);
      setFilteredDrivers([]);
    } finally {
      setLoading(false);
      setHasFetched(true);
    }
  };

  const handleViewDriver = async (driverId) => {
    const toastId = toast.loading('Loading driver details...', { duration: Infinity });
    try {
      const driver = drivers.find(d => d.driver_id === driverId);
      if (!driver) throw new Error('Driver not found');
      await onViewAnalytics(driver.driver_id, entityId, 1);
      toast.dismiss(toastId);
      toast.success('Driver details loaded successfully');
      navigate(`?view=analytic&driver_id=${driverId}`);
    } catch (error) {
      toast.dismiss(toastId);
      toast.error('Failed to load driver details');
      console.error('Error viewing driver:', error);
    }
  };

  const handleViewAndLoadMVR = async (driverId, state) => {
    if (mvrCompleted[driverId]) {
      await handleViewDriver(driverId);
      return;
    }

    const toastId = toast.loading('Loading driver report...', { duration: Infinity });
    try {
      setMvrLoading(driverId);
      setMvrProgress(prev => ({ ...prev, [driverId]: 0 }));

      const simulateProgress = (progress) => {
        return new Promise(resolve => {
          setTimeout(() => {
            setMvrProgress(prev => ({ ...prev, [driverId]: progress }));
            resolve();
          }, 200);
        });
      };

      toast.dismiss(toastId);
      const mvrToastId = toast.loading('Fetching MVR data...', { duration: Infinity });

      await simulateProgress(25);
      const mvrResult = await fetchDriverMVR(driverId, state);
      await simulateProgress(75);

      if (!mvrResult) throw new Error('No MVR data received');

      await simulateProgress(100);
      setMvrCompleted(prev => ({ ...prev, [driverId]: true }));

      toast.dismiss(mvrToastId);
      const viewToastId = toast.loading('Loading driver details...', { duration: Infinity });

      const driver = drivers.find(d => d.driver_id === driverId);
      if (!driver) throw new Error('Driver not found');

      await onViewAnalytics(driver.driver_id, entityId, 1);

      toast.dismiss(viewToastId);
      toast.success('Driver report loaded successfully!');
      navigate(`?view=analytic&driver_id=${driverId}`);
    } catch (error) {
      console.error("Error in combined operation:", error);
      toast.error(error.message === 'Server error - please try again later'
        ? 'Server error - please try again'
        : 'Failed to load driver report'
      );
    } finally {
      setMvrLoading(null);
      setMvrProgress(prev => ({ ...prev, [driverId]: undefined }));
    }
  };

  const getTabColor = (tabId) => {
    const tab = TABS.find(t => t.id === tabId);
    return tab?.color || 'blue';
  };

  const currentColor = getTabColor(activeTab);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback}>
      <>
        <Toaster position="top-right" />
        <div className="p-6 max-w-[1400px] mx-auto">
          {/* Header with Tabs */}
          <div className="flex flex-col space-y-4 mb-6">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <h1 className="flex items-center gap-3 text-2xl font-semibold">
                  <div className={`p-2 bg-${currentColor}-50 rounded-lg`}>
                    <MdPeople className={`text-${currentColor}-600`} />
                  </div>
                  <span>Drivers</span>
                </h1>
                <div className="flex items-center gap-2 px-3 py-1 bg-gray-50 rounded-full text-xs font-medium">
                  <span className="text-gray-500">Showing:</span>
                  <span className={`text-${currentColor}-600`}>{filteredDrivers.length}</span>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 p-1 bg-gray-100 rounded-lg w-fit">
              {TABS.map((tab) => {
                const isActive = activeTab === tab.id;
                const Icon = tab.icon;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium transition-all duration-200 ${
                      isActive
                        ? `bg-white text-${tab.color}-700 shadow-sm`
                        : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
                    }`}
                  >
                    <Icon className={isActive ? `text-${tab.color}-600` : 'text-gray-400'} size={14} />
                    {tab.label}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Status Messages */}
          {error && (
            <div className="mb-6 p-4 rounded-lg bg-red-50 border border-red-100 text-red-600">
              ❌ {error}
            </div>
          )}

          {loading && (
            <div className="text-center py-12">
              <div className={`inline-block animate-spin rounded-full h-8 w-8 border-4 border-${currentColor}-500 border-r-transparent`}></div>
              <p className="mt-4 text-gray-500">Fetching drivers...</p>
            </div>
          )}

          {!loading && hasFetched && drivers.length === 0 && !error && (
            <div className="text-center py-12">
              <p className="text-gray-500">No drivers found</p>
              {activeTab === 'completed' && (
                <p className="text-sm text-gray-400 mt-2">Drivers will appear here once they have been scored</p>
              )}
            </div>
          )}

          {/* Drivers List */}
          {!loading && drivers.length > 0 && (
            <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
              {/* Table Header */}
              <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200">
                <div className="flex items-center px-6 py-4">
                  <div className="w-[25%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Driver</div>
                  <div className="w-[18%] text-xs font-semibold text-gray-700 uppercase tracking-wider">License</div>
                  <div className="w-[10%] text-xs font-semibold text-gray-700 uppercase tracking-wider">State</div>
                  {activeTab === 'completed' ? (
                    <>
                      <div className="w-[10%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Score</div>
                      <div className="w-[12%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Risk Level</div>
                      <div className="w-[13%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Scored At</div>
                    </>
                  ) : (
                    <>
                      <div className="w-[15%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Last Updated</div>
                      <div className="w-[15%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Download MVR</div>
                    </>
                  )}
                  <div className="w-[12%] text-xs font-semibold text-gray-700 uppercase tracking-wider">Actions</div>
                </div>
              </div>

              {/* Rows */}
              <div className="divide-y divide-gray-100">
                {filteredDrivers.map((driver) => (
                  <div
                    key={driver.driver_id}
                    className={`flex items-center px-6 py-4 hover:bg-${currentColor}-50/30 transition-all duration-200 group relative`}
                  >
                    {/* Left border indicator */}
                    <div className={`absolute left-0 top-0 bottom-0 w-1 bg-transparent group-hover:bg-${currentColor}-400 transition-all duration-300`} />

                    {/* Driver Info */}
                    <div className="w-[25%] flex items-center gap-3 pr-4">
                      <div className="relative flex-shrink-0">
                        <div className={`h-10 w-10 rounded-xl bg-gradient-to-br ${
                          activeTab === 'completed'
                            ? 'from-green-500 to-emerald-600'
                            : 'from-blue-500 to-purple-600'
                        } flex items-center justify-center text-white text-sm font-bold shadow-lg group-hover:scale-105 transition-transform duration-200`}>
                          {driver.first_name?.[0]}{driver.last_name?.[0]}
                        </div>
                        {activeTab === 'completed' && (
                          <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-white shadow-sm bg-green-400" />
                        )}
                      </div>
                      <div className="flex flex-col min-w-0 flex-1">
                        <span className={`text-sm font-semibold text-gray-900 group-hover:text-${currentColor}-700 transition-colors truncate`}>
                          {driver.first_name} {driver.last_name}
                        </span>
                        {activeTab === 'completed' && (
                          <span className="text-xs text-gray-500 truncate">Completed</span>
                        )}
                      </div>
                    </div>

                    {/* License Number */}
                    <div className="w-[18%] pr-4">
                      <div className="inline-flex items-center px-2.5 py-1 rounded-lg bg-gray-50 border border-gray-200 max-w-full">
                        <span className="text-xs font-mono text-gray-700 truncate">
                          {driver.license_number}
                        </span>
                      </div>
                    </div>

                    {/* State */}
                    <div className="w-[10%] pr-4">
                      <div className="inline-flex items-center px-2.5 py-1 rounded-full bg-blue-50 border border-blue-100">
                        <span className="text-xs font-bold text-blue-700">
                          {driver.license_state}
                        </span>
                      </div>
                    </div>

                    {activeTab === 'completed' ? (
                      <>
                        {/* Score */}
                        <div className="w-[10%] pr-4">
                          <div className="inline-flex items-center px-3 py-1 rounded-full bg-purple-50 border border-purple-100">
                            <span className="text-sm font-bold text-purple-700">
                              {driver.score_info?.score || 'N/A'}
                            </span>
                          </div>
                        </div>

                        {/* Risk Level */}
                        <div className="w-[12%] pr-4">
                          <RiskBadge level={driver.score_info?.risk_level || 'N/A'} size="md" />
                        </div>

                        {/* Scored Date */}
                        <div className="w-[13%] pr-4">
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-gray-700">
                              {driver.score_info?.scored_at ? new Date(driver.score_info.scored_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric'
                              }) : 'N/A'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {driver.score_info?.scored_at ? new Date(driver.score_info.scored_at).toLocaleDateString('en-US', {
                                year: 'numeric'
                              }) : ''}
                            </span>
                          </div>
                        </div>
                      </>
                    ) : (
                      <>
                        {/* Updated Date */}
                        <div className="w-[15%] pr-4">
                          <div className="flex flex-col">
                            <span className="text-xs font-medium text-gray-700">
                              {driver.updated_at ? new Date(driver.updated_at).toLocaleDateString('en-US', {
                                month: 'short', day: 'numeric'
                              }) : 'N/A'}
                            </span>
                            <span className="text-xs text-gray-500">
                              {driver.updated_at ? new Date(driver.updated_at).toLocaleDateString('en-US', {
                                year: 'numeric'
                              }) : ''}
                            </span>
                          </div>
                        </div>

                        {/* Download MVR */}
                        <div className="w-[15%] pr-4">
                          {driver.order_item_id && driver.status === 'RELEASED' ? (
                            <a href={`${BUSINESS_BASE_URL}/login?l=all-driver-pdf&oi=${driver.order_item_id}&t=${token}`}>
                              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-5 h-5 text-gray-600 hover:text-blue-600">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                              </svg>
                            </a>
                          ) : (
                            <span className="text-xs text-gray-400">N/A</span>
                          )}
                        </div>
                      </>
                    )}

                    {/* Actions */}
                    <div className="w-[12%] flex items-center justify-start gap-2">
                      {activeTab === 'completed' ? (
                        <button
                          onClick={() => handleViewDriver(driver.driver_id)}
                          className="group/btn px-3 py-1.5 rounded-lg text-xs font-semibold transition-all duration-300
                            flex items-center justify-center gap-1.5 border shadow-sm hover:shadow-md transform hover:-translate-y-0.5
                            bg-gradient-to-r from-green-500 to-green-600 text-white border-green-600 hover:from-green-600 hover:to-green-700"
                        >
                          <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                          </svg>
                          View
                        </button>
                      ) : driver.status === 'RELEASED' ? (
                        <button
                          onClick={() => handleViewAndLoadMVR(driver.driver_id, driver.license_state)}
                          disabled={mvrLoading === driver.driver_id}
                          className={`group/btn min-w-[80px] px-2.5 py-1.5 rounded-lg text-xs font-semibold
                            transition-all duration-300 flex items-center justify-center gap-1.5 border shadow-sm
                            hover:shadow-md transform hover:-translate-y-0.5 ${mvrCompleted[driver.driver_id]
                              ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white border-blue-600 hover:from-blue-600 hover:to-blue-700'
                              : 'bg-gradient-to-r from-indigo-500 to-indigo-600 text-white border-indigo-600 hover:from-indigo-600 hover:to-indigo-700'
                            } disabled:opacity-50 disabled:cursor-not-allowed disabled:transform-none`}
                        >
                          {mvrLoading === driver.driver_id ? (
                            <div className="w-full flex flex-col items-center gap-1">
                              <div className="w-full h-1 bg-white/30 rounded-full overflow-hidden">
                                <div className="h-full bg-white rounded-full transition-all duration-300" style={{ width: `${mvrProgress[driver.driver_id] || 0}%` }} />
                              </div>
                              <span className="text-[9px]">Loading...</span>
                            </div>
                          ) : mvrCompleted[driver.driver_id] ? (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                              View
                            </>
                          ) : (
                            <>
                              <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                              </svg>
                              Run
                            </>
                          )}
                        </button>
                      ) : (
                        <span className="text-xs text-gray-400">N/A</span>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Footer */}
              <div className="bg-gray-50 border-t border-gray-200 px-6 py-3">
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span>Showing {filteredDrivers.length} driver{filteredDrivers.length !== 1 ? 's' : ''}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </>
    </ErrorBoundary>
  );
}
